import React, { useState, useEffect, useRef } from 'react';
import { GlossaryTermModel, SlideObject } from '@/components/admin/SlideShow/types';
import useSlideShowContext from '@/components/admin/SlideShow/stores/useSlideShowContext';
import TooltipButton from '@/components/common/HtmlElements/TooltipButton/TooltipButton';
import ITooltipButton from '@/components/interfaces/graphql/HtmlElement/TooltipButton';
import TooltipButtonSnippet from '@/components/common/HtmlElements/TooltipButton/Snippet';
import cn from '@/utils/cn';
import AddImage from '@/components/admin/ImageManager/Add';
import EditImage from 'components/admin/ImageManager/Edit';
import { useModalManager } from '@/components/common/Modal';
import useSlideShowImageFetcher from '@/components/admin/SlideShow/Utils/useSlideShowImageFetcher';
import { IImageModel } from '@/components/admin/SlideShow/stores/useSlideShowStore';
import useRenderExampleSentence from '@/components/admin/SlideShow/Utils/useRenderExampleSentence';
import VocabTableImage from './VocabTableImage';

interface VocabTableProps {
  linkedVocab: GlossaryTermModel[];
  slideObject: SlideObject;
}

const VocabTable = ({ linkedVocab = [], slideObject }: VocabTableProps) => {
  const [tooltipPositions, setTooltipPositions] = useState([]);
  const [glossaryTermModelId, setGlossaryTermModelId] = useState(0);
  const [imageModelToEdit, setImageModelToEdit] = useState<IImageModel>(null);

  const buttonRefs = useRef([]);
  const { fetchImages } = useSlideShowImageFetcher();
  const modalManager = useModalManager();
  const editModalManager = useModalManager();

  const isPresentMode = useSlideShowContext(state => state.isPresentMode);
  const locale = useSlideShowContext(state => state.locale);
  const updateSlideObject = useSlideShowContext(state => state.updateSlideObject);
  const isAdmin = useSlideShowContext(state => state.isAdmin);
  const renderExampleSentence = useRenderExampleSentence();

  const TooltipComponent = isPresentMode ? TooltipButton : TooltipButtonSnippet;

  const afterSave = (res) => {
    const imageModel = res.length ? res[0] : res;

    // This update refreshes the object after the add image modal is submitted, which will display the given image.
    // @ts-ignore
    updateSlideObject({ ...slideObject, imageModel });
  };

  const dropdownOptions = {
    zoom_type: [
      { label: 'in/out buttons', value: 'in/out buttons' },
      { label: 'none', value: '' },
    ],
  };

  useEffect(() => {
    if (buttonRefs.current.length !== linkedVocab.length || !isPresentMode) return;

    const positions = [];
    buttonRefs.current.forEach((ref) => {
      const slideObjectContainer = ref.closest('.tw-absolute') || ref;

      positions.push({
        left: ref.offsetLeft - (ref.offsetWidth / 2) + slideObjectContainer.offsetLeft,
        top: ref.offsetTop + ref.offsetHeight + slideObjectContainer.offsetTop,
      });
    });

    setTooltipPositions(positions);
  }, [buttonRefs]);

  return (
    <div
      className={cn(
        'tw-h-full tw-w-full tw-overflow-x-hidden',
        '[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:tw-hidden',
      )}
    >
      {/* Vocab Table Wrapper */}
      <div className="tw-flex tw-flex-col tw-items-center tw-p-1">
        {linkedVocab.map((lv, i) => {
          const term = lv?.glossary_term;

          if (!term) return null;

          const hasImage = !!lv.image_en_url;

          const exampleSentence = locale === 'en' ? term.example_sentence_en : term.example_sentence_es;
          const termKeyword = locale === 'en' ? term.term_en : term.term_es;

          const baseBorderClasses = `
            tw-border-solid tw-border-t-[1px] tw-border-l-[1px] tw-border-r-[1px] tw-border-med-grey
          `;

          return (
            // Row Wrapper
            <div
              className={cn(`tw-flex tw-h-fit tw-w-full ${baseBorderClasses}`, {
                // Only add bottom border for last element.
                'tw-border-b-[1px]': i + 1 === linkedVocab.length,
              })}
            >
              {/* Term and Tooltip Button Cell */}
              <div className="tw-p-2 tw-flex tw-flex-col tw-basis-[18%]">
                <div className="tw-flex tw-justify-center tw-flex-col">
                  <strong className="tw-text-[1rem]" dangerouslySetInnerHTML={{ __html: termKeyword }} />
                  <div
                    ref={el => buttonRefs.current.push(el)}
                    className={cn('tw-py-2 vocab-button-wrapper', {
                      'tw-w-2': isPresentMode,
                    })}
                  >
                    <TooltipComponent tooltipButton={{
                      data: {
                        autoHide: true,
                        buttonTextEn: 'Definition',
                        buttonTextEs: 'Definición',
                        tooltipStyles: tooltipPositions[i],
                      },
                      htmlEn: term.definition_en,
                      htmlEs: term.definition_es,
                      id: term.id,
                      modelId: slideObject.id,
                      modelType: 'SlideObject',
                      type: 'TooltipButton',
                    } as ITooltipButton}
                    />
                  </div>
                </div>
              </div>
              {/* Image or Add Image Button Cell */}
              <VocabTableImage
                editModalManager={editModalManager}
                glossaryTermModel={lv}
                hasImage={hasImage}
                modalManager={modalManager}
                setGlossaryTermModelId={setGlossaryTermModelId}
                setImageModelToEdit={setImageModelToEdit}
                slideObject={slideObject}
              />
              {/* Example Sentence Cell */}
              <div className="tw-w-[58%] tw-p-2 tw-text-[1rem] tw-overflow-hidden">
                {renderExampleSentence(exampleSentence, termKeyword, term.id)}
              </div>
            </div>
          );
        })}
      </div>
      {modalManager.open && (
        <AddImage
          fromSlideShow
          afterMergeDuplicates={afterSave}
          afterSubmit={afterSave}
          dontMergeCallback={afterSave}
          dropdownOptions={dropdownOptions}
          excludeFields={['display_size', 'alignment', 'caption_en', 'caption_es']}
          getExistingImages={fetchImages}
          isAdmin={isAdmin}
          modalManager={modalManager}
          modelId={glossaryTermModelId}
          modelType="GlossaryTermModel"
          standaloneButton={false}
        />
      )}
      {editModalManager.open && (
        <EditImage
          fromSlideShow
          afterMergeDuplicates={afterSave}
          afterSubmit={afterSave}
          dontMergeCallback={afterSave}
          excludeFields={['display_size', 'alignment', 'caption_en', 'caption_es']}
          imageModel={imageModelToEdit}
          isAdmin={isAdmin}
          modalManager={editModalManager}
          modelId={glossaryTermModelId}
          modelType="GlossaryTermModel"
          standaloneButton={false}
        />
      )}
    </div>
  );
};

export default VocabTable;
